import { hasOrthopulse } from '@odo/shared-ui/clinical/orthopulse';
import { tiptapDocFromString } from '../dx-report/editor/editorState';
import { EMPTY_TIPTAP_DOC } from '../dx-report/templates';
import { buildMergedRdiPdf } from '@odo/shared-ui/clinical-pdf/rdi/buildRdiPdf';
import { submitRdi, editRdi } from './rdiSubmit';
import {
    createRdiDraft,
    updateRdiDraft,
    deleteRdiDraft
} from '../../../../services/clinicalDrafts';
import { extractRdiFormDataFromEvent } from '@odo/shared-ui/clinical/eventReaders';
import { ALIGNER_WEAR_OPTIONS } from '@odo/shared-ui/clinical/alignerFields';
import type { ClinicalWizardConfig } from '../clinical/clinicalWizardConfig';
import type { RDIFormData, SectionToggles } from '@odo/shared-ui/clinical/types';

const DEFAULT_CADENCE_DAYS = '7';
const ORTHOPULSE_CADENCE_DAYS = '5';

function createDefaults(patientName: string, orthopulseStatus?: string | null): RDIFormData {
    const cadence = hasOrthopulse(orthopulseStatus)
        ? ORTHOPULSE_CADENCE_DAYS
        : DEFAULT_CADENCE_DAYS;
    return {
        date: new Date().toISOString().split('T')[0],
        patientName,
        notes: '',
        notesDoc: EMPTY_TIPTAP_DOC,
        elasticsSchemes: [],
        numberOfAligners: 'All',
        nextAppointment: '',
        alignerWear: 'Full Time',
        dmScanFrequency: cadence,
        alignerChangeFrequency: cadence
    };
}

function mergeWithDefaults(partial: Partial<RDIFormData>, defaults: RDIFormData): RDIFormData {
    const notesStr = partial.notes ?? defaults.notes;
    const notesDoc = partial.notesDoc ?? tiptapDocFromString(notesStr);
    return {
        date: partial.date ?? defaults.date,
        patientName: partial.patientName ?? defaults.patientName,
        notes: notesStr,
        notesDoc,
        elasticsSchemes: partial.elasticsSchemes ?? defaults.elasticsSchemes,
        legacyElastics: partial.legacyElastics ?? defaults.legacyElastics,
        numberOfAligners: partial.numberOfAligners ?? defaults.numberOfAligners,
        nextAppointment: partial.nextAppointment ?? defaults.nextAppointment,
        alignerWear: partial.alignerWear ?? defaults.alignerWear,
        dmScanFrequency: partial.dmScanFrequency ?? defaults.dmScanFrequency,
        alignerChangeFrequency: partial.alignerChangeFrequency ?? defaults.alignerChangeFrequency
    };
}

function isFormValid(data: RDIFormData): boolean {
    return data.date.trim() !== '' && data.patientName.trim() !== '';
}

function buildPdf(
    data: RDIFormData,
    attachment: File | null,
    hasOrthopulseFlag: boolean,
    toggles: SectionToggles,
    attachmentBytes?: Uint8Array | null
): Promise<Blob> {
    return buildMergedRdiPdf(data, attachment, hasOrthopulseFlag, toggles, attachmentBytes);
}

export const RDI_WIZARD_CONFIG: ClinicalWizardConfig<RDIFormData> = {
    kindLabel: 'RDI',
    cancelsEarlierVirtualAppointments: true,
    virtualAppointmentTriggerSeries: 'next',
    successCopy: 'The refinement document has been sent to the practice.',
    nextAppointmentLabel: 'Next Appointment',
    nextAppointmentPlaceholder: 'ex: schd appt on aligner 5',
    alignerWearOptions: ALIGNER_WEAR_OPTIONS,
    getNextAppointment: (data) => data.nextAppointment,
    setNextAppointment: (data, value) => ({ ...data, nextAppointment: value }),
    createDefaults,
    extractFormDataFromEvent: extractRdiFormDataFromEvent,
    mergeWithDefaults,
    isFormValid,
    buildPdf,
    submit: submitRdi,
    edit: editRdi,
    drafts: {
        createDraft: createRdiDraft,
        updateDraft: updateRdiDraft,
        deleteDraft: deleteRdiDraft
    },
    getElasticsSchemes: (data) => data.elasticsSchemes ?? [],
    setElasticsSchemes: (data, elasticsSchemes) => ({ ...data, elasticsSchemes })
};
